import React, { useContext, useState } from 'react';
import { useStyletron } from 'baseui';
import { CustomColumn, StringColumn } from 'baseui/data-table';
import {
	CustomDateColumn,
	STATUS_COLORS,
	EMPTY_STRING,
	AppContext,
	ASSET_LINK_LABEL,
	NONE_LABEL,
	LIGHT_CORAL,
	LIGHT_GREEN,
	BLACK,
	WHITE,
	ERROR_MESSAGES
} from 'commons-ui';
import { linkStyles, buttonStyles } from './listConfigStyle';
import { formatSize } from './listHelper';

export const COL_NAMES = {
	assetName: 'assetName',
	assetType: 'assetType',
	imageUrl: 'imageUrl',
	createdAt: 'createdAt',
	outputImageFormat: 'outputImageFormat',
	finalAssetTag: 'finalAssetTag',
	email: 'email',
	processingStatus: 'processingStatus',
	cdnUrl: 'cdnUrl',
	assetSize: 'assetSize'
};

export const columnConfigs = [
	StringColumn({
		title: 'Asset Name',
		maxWidth: 300,
		mapDataToValue: (data: any) => data[COL_NAMES.assetName] || EMPTY_STRING,
		columnName: COL_NAMES.assetName
	}),
	CustomColumn({
		title: 'Processing Status',
		mapDataToValue: (data: any) => data[COL_NAMES.processingStatus],
		maxWidth: 150,
		renderCell: (params: any) => {
			const { value } = params;
			const text: string = value;
			const [css] = useStyletron();
			return (
				<div
					className={css({
						backgroundColor: `${STATUS_COLORS.get(text)}`,
						color: WHITE,
						height: '30px',
						width: '100%',
						textAlign: 'center'
					})}>
					{text}
				</div>
			);
		}
	}),
	StringColumn({
		title: 'Type',
		maxWidth: 200,
		mapDataToValue: (data: any) => data[COL_NAMES.assetType] || EMPTY_STRING,
		columnName: COL_NAMES.assetType
	}),
	StringColumn({
		title: 'Tag',
		maxWidth: 300,
		mapDataToValue: (data: any) => data[COL_NAMES.finalAssetTag] || EMPTY_STRING,
		columnName: COL_NAMES.finalAssetTag
	}),
	StringColumn({
		title: 'Format',
		maxWidth: 150,
		mapDataToValue: (data: any) => data[COL_NAMES.outputImageFormat] || EMPTY_STRING,
		columnName: COL_NAMES.outputImageFormat
	}),
	CustomColumn({
		title: 'Size',
		mapDataToValue: (data: any) => formatSize(data[COL_NAMES.assetSize]),
		maxWidth: 150,
		renderCell: (params: any) => {
			const { value } = params;
			const text: string = value[0];
			const flag: boolean = value[1];
			const [css] = useStyletron();
			return (
				<div
					className={css({
						backgroundColor: flag ? LIGHT_CORAL : LIGHT_GREEN,
						color: BLACK,
						height: '30px',
						width: '100%',
						textAlign: 'center'
					})}>
					{text}
				</div>
			);
		}
	}),
	CustomColumn({
		title: 'CDN Link',
		minWidth: 360,
		maxWidth: 400,
		mapDataToValue: (data: any) => data[COL_NAMES.cdnUrl],
		renderCell: ({ value }: { value: any }) => {
			const { showAlert } = useContext(AppContext);
			const [css] = useStyletron();
			const [imageLoaded, setImageLoaded] = useState(false);
			const handleCopyLink = async (e: React.MouseEvent<HTMLAnchorElement>): Promise<void> => {
				e.preventDefault();
				if (!imageLoaded) {
					try {
						await navigator.clipboard.writeText(value);
						showAlert('Copied asset cdn link');
					} catch {
						showAlert('Failed to copy asset cdn link', { type: 'error' });
					}
				}
			};
			if (Object.values(ERROR_MESSAGES).includes(value)) {
				return <div>{value}</div>;
			}
			return (
				<div>
					<a
						href={value}
						target='_blank'
						rel='noreferrer'
						onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
							if (!imageLoaded) {
								handleCopyLink(e);
							} else {
								e.preventDefault();
								window.open(value, '_blank');
							}
						}}>
						<button type='button' style={buttonStyles}>
							<img
								src={value}
								alt={EMPTY_STRING}
								className={css({ height: '90px' })}
								onLoad={() => setImageLoaded(true)}
								onError={(e) => {
									const img: HTMLImageElement = e.target as HTMLImageElement;
									img.style.display = NONE_LABEL;
									const link: HTMLElement | null = img.parentElement;
									if (link) {
										link.innerText = ASSET_LINK_LABEL;
										Object.assign(link.style, linkStyles);
									}
								}}
							/>
						</button>
					</a>
				</div>
			);
		}
	}),
	CustomDateColumn({
		title: 'Created At',
		maxWidth: 120,
		columnName: COL_NAMES.createdAt
	}),
	StringColumn({
		title: 'User Email',
		maxWidth: 300,
		mapDataToValue: (data: any) => data[COL_NAMES.email] || EMPTY_STRING,
		columnName: COL_NAMES.email
	})
];

export const DEFAULT_CONFIG = {
	pageDetails: {
		pageNo: 1,
		pageSize: 20
	}
};
